<template>
  <div class="main-map">
    <l-map ref="map" id="leaflet-map" :zoom="zoom" :center="center" :max-bounds="bounds" :min-zoom="5"
           :options="options">
      <l-tile-layer :url="url" :attribution="attribution"/>
      <l-geo-json v-if="region"
                  :geojson="regions[region]"
      />

      <template v-for="(c, index) in displayed_points">
        <l-circle-marker
          :key="index"
          :radius="c.selected ? 10 : 5"
          :stroke="false"
          :fillOpacity="c.selected ? 1 : 0.8"
          :fillColor="`${c.selected ? '#0174f0' : get_color(c)}`"
          :lat-lng="[+c.latitude, +c.longitude]"
          @click="onclick(c)"
        >
          <l-tooltip :options="{ permanent: true, interactive: true }" @click="onclick(c)" v-if="c.tooltip">
            <div :class="`map-tooltip ${c.selected ? 'selected' : ''}`">
              {{ c.tooltip }}
            </div>
          </l-tooltip>
        </l-circle-marker>
      </template>
      <l-control position="bottomleft" v-if="show_legend">
        <div class="map-legend">
          <div v-for="(color, key) in serviceColors" :key="key" class="row">
            <div class="circle" :style="{ backgroundColor: color }"></div>
            <div class="caption">{{ serviceLabels[key] }}</div>
          </div>
        </div>
      </l-control>
    </l-map>

    <p class="light-text">Натисніть на точку, щоб побачити детальну інформацію.</p>
  </div>
</template>

<script>
import {LMap, LTileLayer, LGeoJson, LCircleMarker, LTooltip, LControl} from "vue2-leaflet";
import {latLng, L} from "leaflet";
import "leaflet/dist/leaflet.css";

import * as d3 from "d3"
import * as topojson from "topojson-client";

import { serviceColors, backendToFrontendService } from "@/utils/service-mapping";

export default {
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LCircleMarker,
    LTooltip,
    LControl,
  },

  props: ["localPoints", "region", "show_legend"],

  mounted() {
//    let map = this.$refs.map.mapObject;
//    L.gridLayer({ attribution: false }).addTo(map);
    if (this.$refs.map && this.$refs.map.mapObject) {
      let map = this.$refs.map.mapObject;
      map.fitBounds(this.bounds);
      this.loadData();
    }
  },

  created() {
//    this.stored_points = [...this.points];
  },

  data: function () {
    return {
      zoom: 6,
      url: "https://tiles.stadiamaps.com/tiles/outdoors/{z}/{x}/{y}{r}.png",
      attribution:
        '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/">OpenMapTiles</a> &copy; <a href="http://openstreetmap.org">OpenStreetMap</a> contributors',
      center: latLng(48.3794, 31.1656),
      stored_points: [],
      selected_point: null,
      bounds: [[44.3614785833, 22.0856083513], [52.3350745713, 40.1807890155]],
      options: {
        zoomSnap: 0.5,
      },
      regions: null,
      classes: [],
      // localPoints: [...this.points], // Копія точок, щоб не було проблем з реактивністю
    };
  },

  computed: {
    displayed_points() {
      if (!Array.isArray(this.localPoints) || this.localPoints.length === 0) {
        return [];
      }

      // 🟢 Якщо секція — ЗОЗ (health institutions)
      if (this.$parent.section === 'zoz') {

          const filtered = [...this.localPoints].filter(p => {
              if (!p.latitude || !p.longitude || isNaN(p.latitude) || isNaN(p.longitude)) {
                  return false;
              }
              return true;
          });

          return filtered;
      }

      // 🟠 Якщо секція — ПЗШ (harm_reduction)
      if (this.$parent.section === 'harm_reduction') {

          const unpacked = [...this.localPoints].flatMap(p => {
            if (!p.data || !Array.isArray(p.data) || p.data.length === 0) {
              return [];
            }
            return p.data.map(addr => {
              const groups = addr.vulnerability_groups || [];
              const services = addr.services || p.services || (p.full_data && p.full_data.services) || [];

              return {
                ...addr,
                title: p.title,
                parentUid: p.uid,
                services,
                vulnerability_groups: groups
              };
            });
          });

          const filtered = unpacked.filter(p => {
              if (!p.latitude || !p.longitude || isNaN(p.latitude) || isNaN(p.longitude)) {
                  return false;
              }
              return true;
          });

          return filtered;
      }

      // 🟡 Якщо секція інша – просто повертаємо точки
      return [...this.localPoints];
    },
  },

  methods: {
    onclick(c) {
      if (this.selected_point) {
        this.selected_point.selected = false;
      }

      c.selected = true;
      this.selected_point = c;

      this.$emit("point_selected", c);
      this.$forceUpdate();
    },

    async loadData() {
      let [topodata] = await Promise.all([
        d3.json('/data/ukraine.json'),
      ]);

      let geojson = topojson.feature(topodata, topodata.objects.ukraine_regions);
      this.regions = geojson.features.reduce((o, v) => {
        o[v.properties.name] = v;
        return o;
      }, {});
    },

    get_color(point) {
      let services = point.services || (point.full_data && point.full_data.services) || [];

      if (Array.isArray(services) && services.length > 0) {
        const firstService = services[0];

        // 🛠 ОБОВʼЯЗКОВО МАПИТИ backend → frontend
        const mappedService = backendToFrontendService[firstService] || firstService;

        return serviceColors[mappedService] || "grey";
      }

      return "grey";
    },
  },

};

</script>

<style lang="scss" scoped>
.main-map {
  width: 100%;
  // min-height: 500px;

  #leaflet-map {
    width: 100%;
    height: 70vw;
    max-height: 80vh;
  }


  .map-legend {
    background: white;
    padding: 0.7em;

    display: flex;
    flex-direction: column;
    gap: .1em;

    border-radius: 3px;

    .row {
      display: flex;
      gap: .4em;
      align-items: center;
    }

    .circle {
      width: 10px;
      height: 10px;
      border-radius: 5px;
    }
  }
}
</style>

<style lang="scss">

$outline_col: var(--col-orange);
.leaflet-tooltip {
  background-color: unset;
  border: none;
  padding: 0;
  box-shadow: none;
  font-size: 1em;
  font-family: "Open Sans", Helvetica, Arial, sans-serif;
  color: white;
  font-weight: 600;
  text-shadow: $outline_col 3px 0 0,
  $outline_col 2.83487px 0.981584px 0,
  $outline_col 2.35766px 1.85511px 0,
  $outline_col 1.62091px 2.52441px 0,
  $outline_col 0.705713px 2.91581px 0,
  $outline_col -0.287171px 2.98622px 0px,
  $outline_col -1.24844px 2.72789px 0px,
  $outline_col -2.07227px 2.16926px 0px,
  $outline_col -2.66798px 1.37182px 0px,
  $outline_col -2.96998px 0.42336px 0px,
  $outline_col -2.94502px -0.571704px 0px,
  $outline_col -2.59586px -1.50383px 0px,
  $outline_col -1.96093px -2.27041px 0px,
  $outline_col -1.11013px -2.78704px 0px,
  $outline_col -0.137119px -2.99686px 0px,
  $outline_col 0.850987px -2.87677px 0px,
  $outline_col 1.74541px -2.43999px 0px,
  $outline_col 2.44769px -1.73459px 0px,
  $outline_col 2.88051px -0.838247px 0px;

}

.leaflet-tooltip-top::before,
.leaflet-tooltip-bottom::before,
.leaflet-tooltip-right::before,
.leaflet-tooltip-left::before {
  display: none;
}

.map-tooltip.selected {

}
</style>
